import React, { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { FaqItem } from "../../data/faqContent";
import { FAQ_SUPPORT_EMAIL } from "../../data/faqContent";
import { linkifyInlineText } from "../../utils/linkifyInlineText";

interface FaqAccordionProps {
  items: FaqItem[];
  /** Open a specific item on mount (e.g. deep-link hash) */
  initialOpenId?: string | null;
}

/**
 * Accessible accordion for FAQ Q&A with optional next-step links.
 */
const FaqAccordion: React.FC<FaqAccordionProps> = ({
  items,
  initialOpenId = null,
}) => {
  const baseId = useId();
  const [openId, setOpenId] = useState<string | null>(initialOpenId);

  const toggle = (id: string): void => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const isOpen = openId === item.id;
        const panelId = `${baseId}-${item.id}-panel`;
        const buttonId = `${baseId}-${item.id}-button`;

        return (
          <div
            key={item.id}
            className="rounded-xl border border-theme-border bg-theme-surface-auth overflow-hidden"
          >
            <button
              type="button"
              id={buttonId}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(item.id)}
              className="w-full flex items-start justify-between gap-3 px-4 py-3.5 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors"
            >
              <span className="text-sm sm:text-base font-semibold text-theme-fg leading-snug">
                {item.question}
              </span>
              <span
                className="mt-0.5 shrink-0 text-theme-fg-secondary text-lg leading-none"
                aria-hidden="true"
              >
                {isOpen ? "−" : "+"}
              </span>
            </button>
            {isOpen && (
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                className="px-4 pb-4 pt-0 border-t border-theme-border"
              >
                <div className="mt-3 text-sm text-theme-fg-secondary leading-relaxed space-y-3">
                  {item.steps && item.steps.length > 0 ? (
                    <>
                      {item.answer.trim().length > 0 && (
                        <p>{linkifyInlineText(item.answer)}</p>
                      )}
                      <ol className="list-decimal list-outside ms-5 space-y-2 marker:text-theme-fg-secondary">
                        {item.steps.map((step, index) => (
                          <li key={`${item.id}-step-${index}`} className="ps-1">
                            {linkifyInlineText(step)}
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : (
                    <p className="whitespace-pre-wrap">
                      {linkifyInlineText(item.answer)}
                    </p>
                  )}
                </div>
                {item.action &&
                  (item.action.external ? (
                    <a
                      href={item.action.href}
                      target={item.action.href.startsWith("mailto:") ? undefined : "_blank"}
                      rel={
                        item.action.href.startsWith("mailto:")
                          ? undefined
                          : "noopener noreferrer"
                      }
                      className="inline-flex mt-3 text-sm font-medium text-theme-link-primary hover:text-theme-link-primary-hover hover:underline"
                    >
                      {item.action.label} →
                    </a>
                  ) : (
                    <Link
                      to={item.action.href}
                      className="inline-flex mt-3 text-sm font-medium text-theme-link-primary hover:text-theme-link-primary-hover hover:underline"
                    >
                      {item.action.label} →
                    </Link>
                  ))}
              </div>
            )}
          </div>
        );
      })}

      <p className="pt-2 text-xs sm:text-sm text-theme-fg-secondary leading-relaxed">
        Still need help after trying these steps? Email{" "}
        <a
          href={`mailto:${FAQ_SUPPORT_EMAIL}`}
          className="font-medium text-theme-link-primary hover:underline"
        >
          {FAQ_SUPPORT_EMAIL}
        </a>
        .
      </p>
    </div>
  );
};

export default FaqAccordion;
